<template>
  <div>
    <div class="section-head">
      <h4>Fuvarok ({{ shipments.length }})</h4>
      <el-button
        v-can="'desadv.update'"
        type="success"
        size="small"
        icon="el-icon-plus"
        :loading="adding"
        :disabled="!!addBlockReason"
        :title="addBlockReason"
        @click="addShipment"
      >
        Új fuvar
      </el-button>
    </div>

    <div
      v-if="!shipments.length"
      class="empty-hint"
    >
      Még nincs fuvar. Hozzon létre egyet a véglegesített raklapok elosztásához.
    </div>

    <el-card
      v-for="shipment in shipments"
      :key="shipment.id"
      class="shipment-card"
      shadow="never"
    >
      <div
        slot="header"
        class="shipment-head"
      >
        <span>
          {{ shipment.position }}. fuvar
          <el-tag
            size="small"
            :type="shipment.status === 'closed' ? 'success' : 'info'"
            style="margin-left: 8px;"
          >
            {{ shipment.status === 'closed' ? 'Lezárva' : 'Nyitott' }}
          </el-tag>
          <span
            v-if="shipment.despatch_advice_number"
            class="despatch-number"
          >
            Bizonylatszám: {{ shipment.despatch_advice_number }}
          </span>
          <el-tag
            v-if="shipment.status === 'closed'"
            size="mini"
            :type="sendStatusTagType(shipment)"
            style="margin-left: 8px;"
          >
            {{ sendStatusLabel(shipment) }}
          </el-tag>
        </span>
        <span
          v-if="shipment.status === 'open'"
          class="shipment-actions"
        >
          <el-link
            v-can="'desadv.update'"
            type="warning"
            :underline="false"
            icon="el-icon-circle-check"
            :disabled="saving"
            @click="closeShipment(shipment)"
          >
            Lezárás
          </el-link>
          <el-link
            v-can="'desadv.update'"
            type="danger"
            :underline="false"
            icon="el-icon-delete"
            :disabled="saving"
            @click="deleteShipment(shipment)"
          >
            Törlés
          </el-link>
        </span>
        <span
          v-else-if="canResend(shipment)"
          class="shipment-actions"
        >
          <el-link
            v-can="'desadv.update'"
            type="primary"
            :underline="false"
            icon="el-icon-position"
            :disabled="sending === shipment.id"
            @click="sendShipment(shipment)"
          >
            Kiküldés újra
          </el-link>
        </span>
      </div>

      <!-- why a closed truck is still sitting here -->
      <el-alert
        v-if="shipment.send_status === 'failed' && sendError(shipment)"
        :title="sendError(shipment).title || 'EDI kiküldési hiba'"
        :description="sendError(shipment).message"
        type="error"
        show-icon
        :closable="false"
        style="margin-bottom: 12px;"
      />

      <!-- open: tick the finalized pallets onto this truck -->
      <template v-if="shipment.status === 'open'">
        <div class="shipment-date">
          <span class="shipment-date-label">Szállítás dátuma:</span>
          <el-date-picker
            v-model="deliveryDates[shipment.id]"
            type="date"
            size="small"
            value-format="yyyy-MM-dd"
            style="width: 160px;"
            :disabled="!can('desadv.update') || savingDate === shipment.id"
            @change="saveDeliveryDate(shipment)"
          />
        </div>
        <div
          v-if="!finalizedPallets.length"
          class="empty-hint"
        >
          Nincs véglegesített raklap.
        </div>
        <div
          v-for="pallet in finalizedPallets"
          :key="pallet.id"
          class="pallet-row"
        >
          <el-checkbox
            :value="isSelected(shipment, pallet)"
            :disabled="!can('desadv.update') || isLockedElsewhere(shipment, pallet)"
            @change="checked => togglePallet(shipment, pallet, checked)"
          >
            {{ palletLabel(pallet) }}
            <span
              v-if="palletHint(shipment, pallet)"
              class="pallet-hint"
            >
              {{ palletHint(shipment, pallet) }}
            </span>
          </el-checkbox>
        </div>
        <div class="shipment-totals">
          Kiválasztva: {{ (selections[shipment.id] || []).length }} raklap
        </div>
      </template>

      <!-- closed: locked pallet list + totals -->
      <template v-else>
        <div
          v-for="pallet in shipment.pallets"
          :key="pallet.id"
          class="pallet-row"
        >
          {{ palletLabel(pallet) }}
        </div>
        <div class="shipment-totals">
          {{ shipment.total_pallets }} raklap ·
          {{ shipment.total_packages }} csomag ·
          {{ shipment.gross_weight }} kg ·
          Szállítás dátuma: {{ shipment.delivery_date ? shipment.delivery_date.substr(0, 10) : '—' }}
        </div>
      </template>
    </el-card>
  </div>
</template>

<script>
export default {
  props: {
    desadv: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      adding: false,
      saving: false,
      savingDate: null,
      sending: null,
      sendPollTimer: null,
      sendPollsLeft: 0,
      // shipment id -> pallet ids ticked onto that truck (the full desired set)
      selections: {},
      // shipment id -> delivery date (yyyy-MM-dd), persisted on picker change
      deliveryDates: {},
    };
  },
  computed: {
    shipments() {
      return this.desadv.shipments || [];
    },
    notFinalized() {
      return this.desadv.status === 'open';
    },
    addBlockReason() {
      if (this.hasOpenShipment) return 'Előbb zárja le a folyamatban lévő fuvart.';
      if (!this.notFinalized) return '';
      const pallets = this.desadv.pallets || [];
      if (!pallets.length) return 'Fuvar létrehozásához legalább egy raklap szükséges.';
      if (pallets.some(p => !p.finalized_at)) return 'Előbb véglegesítse az összes raklapot.';
      return '';
    },
    // Only finalized pallets can be loaded onto a truck.
    finalizedPallets() {
      return (this.desadv.pallets || []).filter(p => p.finalized_at);
    },
    // Only one truck is loaded at a time: a new one may start once this is closed.
    hasOpenShipment() {
      return this.shipments.some(s => s.status === 'open');
    },
    hasQueuedSend() {
      return this.shipments.some(s => s.send_status === 'queued');
    },
  },
  watch: {
    desadv: {
      immediate: true,
      handler() {
        this.resetSelections();
        this.resetDeliveryDates();
      },
    },
    hasQueuedSend: {
      immediate: true,
      handler(queued) {
        if (queued) {
          this.startSendPolling();
        } else {
          this.stopSendPolling();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopSendPolling();
  },
  methods: {
    can(permission) {
      const user = this.$store.getters['auth/user'];
      return user && user.permissions && user.permissions.includes(permission);
    },
    resetSelections() {
      const selections = {};
      this.shipments.forEach((shipment) => {
        // Ticks are only persisted on close, so keep the in-progress ones on an
        // open shipment across a refresh; closed shipments take the server state.
        selections[shipment.id] = shipment.status === 'open' && this.selections[shipment.id]
          ? this.selections[shipment.id]
          : (shipment.pallets || []).map(p => p.id);
      });
      this.selections = selections;
    },
    // Dates are persisted immediately, so the server state always wins here.
    resetDeliveryDates() {
      const dates = {};
      this.shipments.forEach((shipment) => {
        dates[shipment.id] = shipment.delivery_date
          ? shipment.delivery_date.substr(0, 10)
          : null;
      });
      this.deliveryDates = dates;
    },
    async saveDeliveryDate(shipment) {
      this.savingDate = shipment.id;
      try {
        await this.$store.dispatch('desadv/updateShipment', {
          id: shipment.id,
          // The key must be sent even when empty: null clears the date.
          payLoad: { delivery_date: this.deliveryDates[shipment.id] || null },
        });
        this.$emit('refresh');
      } catch (e) {
        this.resetDeliveryDates();
      }
      this.savingDate = null;
    },
    // The newest still open send failure of the truck, or null.
    sendError(shipment) {
      return (shipment.send_errors || [])[0] || null;
    },
    sendStatusLabel(shipment) {
      switch (shipment.send_status) {
        case 'sent':
          return `EDI kiküldve: ${this.$options.filters.dateTime(shipment.sent_at)}`;
        case 'queued':
          return 'EDI kiküldés folyamatban…';
        case 'failed':
          return 'EDI kiküldés sikertelen';
        default:
          return 'EDI nincs kiküldve';
      }
    },
    sendStatusTagType(shipment) {
      return { sent: 'success', queued: 'info', failed: 'danger' }[shipment.send_status] || 'warning';
    },
    // A send in flight decides its own outcome; only a settled one is resendable.
    canResend(shipment) {
      return shipment.send_status !== 'queued' && !shipment.sent_at;
    },
    async sendShipment(shipment) {
      this.sending = shipment.id;
      try {
        await this.$store.dispatch('desadv/sendShipment', shipment.id);
        // Comes back as 'queued'; the outcome arrives with one of the polls.
        this.$emit('refresh');
      } catch (e) {
      }
      this.sending = null;
    },
    // A queued send finishes in the background, so the detail is reloaded until
    // it settles. Bounded: a job nothing ever picks up must not poll forever.
    startSendPolling() {
      if (this.sendPollTimer) {
        return;
      }
      this.sendPollsLeft = 20;
      this.sendPollTimer = setInterval(() => {
        if (this.sendPollsLeft-- < 1) {
          this.stopSendPolling();
          return;
        }
        this.$emit('refresh');
      }, 3000);
    },
    stopSendPolling() {
      if (this.sendPollTimer) {
        clearInterval(this.sendPollTimer);
      }
      this.sendPollTimer = null;
    },
    palletLabel(pallet) {
      const type = pallet.pallet_product ? ` · ${pallet.pallet_product.name}` : '';
      return `${pallet.position}. raklap${type} · SSCC: ${pallet.sscc}`;
    },
    isSelected(shipment, pallet) {
      return (this.selections[shipment.id] || []).includes(pallet.id);
    },
    // The shipment (other than the given one) currently holding the pallet.
    holderOf(shipment, pallet) {
      return this.shipments.find(s => s.id !== shipment.id
        && (this.selections[s.id] || []).includes(pallet.id));
    },
    // A pallet on a closed shipment is locked; on an open one it stays movable.
    isLockedElsewhere(shipment, pallet) {
      const holder = this.holderOf(shipment, pallet);
      return !!(holder && holder.status === 'closed');
    },
    palletHint(shipment, pallet) {
      const holder = this.holderOf(shipment, pallet);
      if (!holder) return '';
      return holder.status === 'closed'
        ? `(lezárt fuvaron: ${holder.position}.)`
        : `(jelenleg: ${holder.position}. fuvar)`;
    },
    togglePallet(shipment, pallet, checked) {
      const selected = (this.selections[shipment.id] || []).filter(id => id !== pallet.id);
      if (checked) {
        selected.push(pallet.id);
        // Moving: untick it from any other open shipment locally.
        this.shipments.forEach((s) => {
          if (s.id !== shipment.id && s.status === 'open') {
            this.$set(this.selections, s.id, (this.selections[s.id] || []).filter(id => id !== pallet.id));
          }
        });
      }
      this.$set(this.selections, shipment.id, selected);
    },
    async addShipment() {
      if (this.notFinalized) {
        try {
          await this.$confirm('A fuvar létrehozása lezárja a raklapozást.', 'Új fuvar', {
            confirmButtonText: 'Fuvar létrehozása',
            cancelButtonText: 'Mégse',
            type: 'warning',
          });
        } catch (e) {
          return;
        }
      }
      this.adding = true;
      try {
        if (this.notFinalized) {
          await this.$store.dispatch('desadv/finalize', this.desadv.id);
        }
        await this.$store.dispatch('desadv/createShipment', this.desadv.id);
      } catch (e) {
      }
      this.adding = false;
      this.$emit('refresh');
    },
    closeShipment(shipment) {
      if (!(this.selections[shipment.id] || []).length) {
        this.$messageWrapper.warning('Üres fuvar nem zárható le. Jelöljön ki legalább egy raklapot.');
        return;
      }
      // Dates are persisted immediately, so the field is the truth here. The
      // backend refuses the close without one: it dates the customer sale.
      if (!this.deliveryDates[shipment.id]) {
        this.$messageWrapper.warning('A fuvar lezárásához adja meg a szállítás dátumát.');
        return;
      }
      this.$confirm(
        'A lezárás kiosztja a fuvar bizonylatszámát, és a raklap-hozzárendelés többé nem módosítható. Folytatja?',
        'Fuvar lezárása',
        { confirmButtonText: 'Lezárás', cancelButtonText: 'Mégse', type: 'warning' },
      ).then(async () => {
        this.saving = true;
        try {
          // Persist the current ticks first, then close.
          await this.$store.dispatch('desadv/assignShipmentPallets', {
            id: shipment.id,
            palletIds: this.selections[shipment.id] || [],
          });
          await this.$store.dispatch('desadv/closeShipment', shipment.id);
          this.$emit('refresh');
        } catch (e) {
        }
        this.saving = false;
      }).catch(() => {});
    },
    deleteShipment(shipment) {
      this.$confirm(`Biztosan törli a(z) ${shipment.position}. fuvart? A raklapjai visszakerülnek a szabadok közé.`, 'Megerősítés', {
        confirmButtonText: 'Törlés',
        cancelButtonText: 'Mégse',
        type: 'warning',
      }).then(async () => {
        await this.$store.dispatch('desadv/deleteShipment', shipment.id);
        this.$emit('refresh');
      }).catch(() => {});
    },
  },
};
</script>
<style scoped>
  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .shipment-card {
    margin-bottom: 16px;
  }
  .shipment-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .shipment-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .despatch-number {
    margin-left: 12px;
    font-size: 13px;
    color: #909399;
  }
  .shipment-date {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .shipment-date-label {
    font-size: 13px;
    color: #909399;
  }
  .pallet-row {
    padding: 2px 0;
  }
  .pallet-hint {
    font-size: 12px;
    color: #909399;
  }
  .shipment-totals {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }
  .empty-hint {
    font-size: 13px;
    color: #909399;
    margin-bottom: 12px;
  }
</style>
